import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Image,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { productTemplateApi } from '../../apis/productTemplate';
import { ProductSizeMap, ProductTypeLabelMap } from '../../constants/order';
import {
  IProductTemplate,
  IProductTemplateVariant,
  ProductType,
} from '../../interfaces/Order';
import { PaginationParamsResponse } from '../../interfaces/PaginationParamsResponse';
import { RootState } from '../../reducers';
import { Role } from '../../role/role';
import { PageContainer } from '../../styles/Global';
import PageHeader from '../PageHeader/PageHeader';

const { Search } = Input;

const ProductTemplateList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginationParamsResponse<IProductTemplate>>({
    items: [],
    page: 1,
    pageCount: 0,
    perPage: 20,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const { authUser } = useSelector((state: RootState) => state.auth);
  const isAdmin =
    authUser?.role === Role.ADMIN || authUser?.role === Role.MASTER_ADMIN;

  const fetchProductTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const result = await productTemplateApi.getProductTemplates({
        page: data.page,
        perPage: data.perPage,
      });

      // Apply client-side filtering for mock data
      let filteredData = result.items;

      if (searchTerm) {
        filteredData = filteredData.filter(
          (item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.description
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item.color.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setData({
        items: filteredData,
        page: data.page,
        pageCount: Math.ceil(filteredData.length / data.perPage),
        perPage: data.perPage,
        total: filteredData.length,
      });
    } catch (error) {
      message.error('Không thể tải danh sách mẫu sản phẩm');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [data.page, data.perPage, searchTerm]);

  useEffect(() => {
    fetchProductTemplates();
  }, [fetchProductTemplates]);

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa mẫu sản phẩm này?',
      content: 'Hành động này không thể hoàn tác.',
      okText: 'Có, Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await productTemplateApi.deleteProductTemplate(id);
          message.success('Xóa mẫu sản phẩm thành công');
          fetchProductTemplates();
        } catch (error) {
          message.error('Không thể xóa mẫu sản phẩm');
          console.error(error);
        }
      },
    });
  };

  const handleEdit = (template: IProductTemplate) => {
    // Navigate to detail page for editing
    navigate(`/product-templates/${template.id}`);
  };

  const handleCreate = () => {
    navigate('/product-templates/new');
  };

  const columns: ColumnsType<IProductTemplate> = [
    {
      title: 'Hình ảnh',
      width: 80,
      render: (_, record: IProductTemplate) =>
        record.frontTemplateUrl || record.backTemplateUrl ? (
          <Image
            width={50}
            height={50}
            src={record.frontTemplateUrl ?? record.backTemplateUrl ?? ''}
            alt="Mẫu sản phẩm"
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <div
            style={{
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              border: '1px dashed #d9d9d9',
              borderRadius: 4,
              fontSize: '10px',
              color: '#999',
              textAlign: 'center',
            }}
          >
            Chưa có ảnh
          </div>
        ),
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
    },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (type: ProductType) => {
        return <Tag color="blue">{ProductTypeLabelMap[type]}</Tag>;
      },
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      render: (color: string, record: IProductTemplate) => (
        <Space>
          <div
            style={{
              width: 20,
              height: 20,
              backgroundColor: record.colorHexCode,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
            }}
          />
          {color}
        </Space>
      ),
    },
    {
      title: 'Kích cỡ có sẵn',
      dataIndex: 'variants',
      key: 'variants',
      render: (variants: IProductTemplateVariant[]) => (
        <Space wrap>
          {variants.map((variant) => (
            <Tag key={variant.id} color="green">
              {ProductSizeMap[variant.size]}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Giá',
      dataIndex: 'variants',
      key: 'price',
      render: (variants: IProductTemplateVariant[]) => {
        if (!variants || variants.length === 0) return '-';
        const prices = Array.from(new Set(variants.map((v) => v.price)));
        if (prices.length === 1) {
          return `${prices[0].toLocaleString('vi-VN')}₫`;
        }
        return `${Math.min(...prices).toLocaleString('vi-VN')}₫ - ${Math.max(
          ...prices
        ).toLocaleString('vi-VN')}₫`;
      },
      sorter: true,
    },
    {
      title: 'Tồn kho',
      dataIndex: 'variants',
      key: 'stockQuantity',
      render: (variants: IProductTemplateVariant[]) => {
        if (!variants || variants.length === 0) return '-';
        const totalStock = variants.reduce(
          (acc, variant) => acc + variant.stockQuantity,
          0
        );
        return (
          <Tag
            color={
              totalStock > 50 ? 'green' : totalStock > 20 ? 'orange' : 'red'
            }
          >
            {totalStock}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: Date) => new Date(date).toLocaleDateString('vi-VN'),
      sorter: true,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      render: (_, record: IProductTemplate) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={!isAdmin}
            />
          </Tooltip>
          {/* <Tooltip title="Xóa">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              disabled={!isAdmin}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Mẫu sản phẩm"
        // addButton={
        //   isAdmin ? (
        //     <PrimaryButton
        //       type="primary"
        //       icon={<PlusOutlined />}
        //       onClick={handleCreate}
        //     >
        //       Thêm mẫu sản phẩm
        //     </PrimaryButton>
        //   ) : undefined
        // }
      />

      <Space
        direction="vertical"
        size="large"
        style={{ width: '100%', marginBottom: 16 }}
      >
        <Space wrap>
          <Search
            placeholder="Tìm kiếm mẫu sản phẩm..."
            allowClear
            style={{ width: 300 }}
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchProductTemplates}
            loading={loading}
          >
            Tải lại
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={handleCreate}
            disabled={!isAdmin}
          >
            Thêm mẫu sản phẩm
          </Button>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={data.items}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          current: data.page,
          pageSize: data.perPage,
          total: data.total,
        }}
      />
    </PageContainer>
  );
};

export default ProductTemplateList;
